// @ts-nocheck
// TODO: tighten types after migration
/**
 * Utility functions for blog post calculations
 */

import { getReadingTime } from "../utils/getReadingTime";

/**
 * Calculate reading time based on word count
 * @param {number} wordCount - Number of words in the content
 * @param {number} wordsPerMinute - Average reading speed (default: 200 words per minute)
 * @returns {number} Reading time in minutes
 */
export const calculateReadingTime = (wordCount, wordsPerMinute = 200) => {
    if (!wordCount || wordCount <= 0) return 0;
    return Math.ceil(wordCount / wordsPerMinute);
};

/**
 * Calculate word count from HTML content
 * @param {string} htmlContent - HTML content string
 * @returns {number} Word count
 */
export const calculateWordCount = (htmlContent) => {
    if (!htmlContent) return 0;
    
    // Remove HTML tags and get text content
    const textContent = htmlContent.replace(/<[^>]*>/g, '');
    
    // Split by whitespace and filter out empty strings
    const words = textContent.trim().split(/\s+/).filter(word => word.length > 0);
    
    return words.length;
};

/**
 * Calculate word count from RichText JSON content
 * @param {Object} richTextJson - RichText JSON object
 * @returns {number} Word count
 */
export const calculateWordCountFromRichText = (richTextJson) => {
    if (!richTextJson) return 0;
    
    // Handle TipTap format (has type: 'doc' and content array)
    if (richTextJson.type === 'doc' && richTextJson.content && Array.isArray(richTextJson.content)) {
        let wordCount = 0;
        
        const extractTextFromNode = (node) => {
            if (node.text) {
                const words = node.text.trim().split(/\s+/).filter(word => word.length > 0);
                wordCount += words.length;
            }
            
            if (node.content && Array.isArray(node.content)) {
                node.content.forEach(child => extractTextFromNode(child));
            }
        };
        
        richTextJson.content.forEach(child => extractTextFromNode(child));
        return wordCount;
    }
    
    // Handle RichText format (has children array)
    if (richTextJson.children && Array.isArray(richTextJson.children)) {
        let wordCount = 0;
        
        const extractTextFromNode = (node) => {
            if (node.text) {
                const words = node.text.trim().split(/\s+/).filter(word => word.length > 0);
                wordCount += words.length;
            }
            
            if (node.children && Array.isArray(node.children)) {
                node.children.forEach(child => extractTextFromNode(child));
            }
        };
        
        richTextJson.children.forEach(child => extractTextFromNode(child));
        return wordCount;
    }
    
    return 0;
};

/**
 * Get reading time and word count for a post
 * @param {Object} post - Post object with content
 * @returns {Object} Object containing readingTime and wordCount
 */
export const getPostMetrics = (post) => {
    let wordCount = 0;
    let readingTime = 0;
    
    // Try to get reading time from the post data first
    if (post.readingTime && post.readingTime > 0) {
        readingTime = post.readingTime;
        // If reading time is provided, estimate word count
        wordCount = readingTime * 200;
    } else {
        // Calculate from content - try multiple formats
        if (post.content) {
            // Handle HTML content (MongoDB format) - check html property first
            if (post.content.html && typeof post.content.html === 'string' && post.content.html.trim().length > 0) {
                readingTime = getReadingTime(post.content.html);
                wordCount = calculateWordCount(post.content.html);
            }
            // Handle RichText JSON content (HyGraph format or TipTap format)
            else if (post.content.json) {
                try {
                    // Handle both TipTap JSON format and RichText format
                    const jsonContent = typeof post.content.json === 'string' 
                        ? JSON.parse(post.content.json) 
                        : post.content.json;
                    wordCount = calculateWordCountFromRichText(jsonContent);
                    readingTime = calculateReadingTime(wordCount);
                } catch (e) {
                    // If JSON parsing fails, try to extract text from string
                    console.warn('Failed to parse JSON content, trying fallback:', e);
                    if (typeof post.content.json === 'string') {
                        readingTime = getReadingTime(post.content.json);
                        wordCount = calculateWordCount(post.content.json);
                    }
                }
            }
            // Handle direct string content (if content is a string)
            else if (typeof post.content === 'string' && post.content.trim().length > 0) {
                readingTime = getReadingTime(post.content);
                wordCount = calculateWordCount(post.content);
            }
            // Handle content.html as direct property (some MongoDB posts)
            else if (post.html && typeof post.html === 'string' && post.html.trim().length > 0) {
                readingTime = getReadingTime(post.html);
                wordCount = calculateWordCount(post.html);
            }
        }
        // Fallback: try to get word count from post data
        else if (post.wordCount && post.wordCount > 0) {
            wordCount = post.wordCount;
            readingTime = calculateReadingTime(wordCount);
        }
        // Additional fallback: check if content exists but wasn't caught above
        else if (post.content && typeof post.content === 'object') {
            // Try to extract HTML from nested structure
            const htmlContent = post.content.html || post.content.HTML || '';
            if (htmlContent && typeof htmlContent === 'string' && htmlContent.trim().length > 0) {
                readingTime = getReadingTime(htmlContent);
                wordCount = calculateWordCount(htmlContent);
            }
        }
    }
    
    // Ensure minimum reading time of 1 minute if content exists (even if calculation returns 0)
    if (readingTime === 0 && post.content && (
        (post.content.html && post.content.html.trim().length > 0) ||
        (post.content.json) ||
        (typeof post.content === 'string' && post.content.trim().length > 0)
    )) {
        readingTime = 1; // Minimum 1 min read for any content
    }
    
    return {
        wordCount,
        readingTime
    };
};

